'use client';

import { useState, useEffect, useRef } from 'react';

interface VideoSplashScreenProps {
  onComplete: () => void;
}

export default function VideoSplashScreen({ onComplete }: VideoSplashScreenProps) {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [showUnmute, setShowUnmute] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRef = useRef(false);

  const handleComplete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    setFadeOut(true);
    setTimeout(() => {
      setVisible(false);
      onComplete();
    }, 600);
  };

  const handleUnmute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = false;
      video.volume = 1;
      setShowUnmute(false);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      handleComplete();
      return;
    }

    const onEnded = () => handleComplete();
    const onError = () => handleComplete();

    const onLoadedMetadata = () => {
      // Set fallback timeout based on actual video duration + 2s buffer
      const duration = video.duration;
      if (duration && isFinite(duration)) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          handleComplete();
        }, (duration + 2) * 1000);
      }
    };

    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);
    video.addEventListener('loadedmetadata', onLoadedMetadata);

    // Initial fallback in case metadata never loads
    timerRef.current = setTimeout(() => {
      handleComplete();
    }, 10000);

    // Try autoplay with full sound
    video.muted = false;
    video.volume = 1;

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Browser policy blocked unmuted autoplay — fallback to muted + show unmute button
        video.muted = true;
        setShowUnmute(true);
        video.play().catch(() => {
          // Autoplay completely blocked — skip splash
          handleComplete();
        });
      });
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
      style={{ transition: 'opacity 0.6s ease-in-out', opacity: fadeOut ? 0 : 1 }}
    >
      <video
        ref={videoRef}
        src="https://res.cloudinary.com/hukx0umi/video/upload/v1783149474/Aero_Logo_Reveal_720p_dckhsk.mp4"
        playsInline
        preload="auto"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
          pointerEvents: 'none',
        }}
      />
      {showUnmute && (
        <button
          onClick={handleUnmute}
          style={{
            position: 'absolute',
            bottom: '40px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.5)',
            borderRadius: '10px',
            color: '#fff',
            padding: '12px 28px',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            backdropFilter: 'blur(6px)',
            zIndex: 10000,
            letterSpacing: '0.02em',
          }}
        >
          🔊 Tap to enable sound
        </button>
      )}
    </div>
  );
}
